import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { Description, Add } from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { generateFlashcardsFromDocument } from '../services/aiService';
import CustomAlert from '../components/CustomAlert';

const DocumentToFlashcardsScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { setId } = route.params || {};
  const { addMultipleFlashcards, useAIFlashcards, canUseAIFlashcards, getRemainingAIFlashcards } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  
  // Add scrollbar styling based on theme - matches background
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'document-scrollbar-style';
    style.textContent = `
      [data-scrollbar-custom="true"] {
        scrollbar-width: thin;
        scrollbar-color: ${theme.surface} ${theme.surface};
      }
      [data-scrollbar-custom="true"]::-webkit-scrollbar {
        width: 8px;
      }
      [data-scrollbar-custom="true"]::-webkit-scrollbar-track {
        background: ${theme.surface};
        border-radius: 4px;
      }
      [data-scrollbar-custom="true"]::-webkit-scrollbar-thumb {
        background-color: ${theme.surface};
        border-radius: 4px;
      }
      [data-scrollbar-custom="true"]::-webkit-scrollbar-thumb:hover {
        background-color: ${theme.border};
      }
    `;
    // Remove existing style if present
    const existing = document.getElementById('document-scrollbar-style');
    if (existing) {
      document.head.removeChild(existing);
    }
    document.head.appendChild(style);
    return () => {
      const toRemove = document.getElementById('document-scrollbar-style');
      if (toRemove) {
        document.head.removeChild(toRemove);
      }
    };
  }, [theme]);
  const [documentText, setDocumentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedCards, setGeneratedCards] = useState([]);
  const [alert, setAlert] = useState({ visible: false, title: '', message: '' });

  const handleGenerate = async () => {
    if (!documentText.trim()) {
      setAlert({
        visible: true,
        title: 'Error',
        message: 'Please enter document content',
      });
      return;
    }

    if (!setId) {
      return;
    }

    setLoading(true);
    setGeneratedCards([]);

    try {
      const flashcards = await generateFlashcardsFromDocument(documentText.trim());
      
      if (flashcards.length === 0) {
        setAlert({
          visible: true,
          title: 'Error',
          message: 'No flashcards were generated. Please try again.',
        });
        setLoading(false);
        return;
      }

      // Check if user can use AI flashcards
      if (!canUseAIFlashcards(flashcards.length)) {
        const remaining = getRemainingAIFlashcards();
        setAlert({
          visible: true,
          title: 'Limit Reached',
          message: `You don't have enough AI flashcards remaining. You have ${remaining} remaining, but need ${flashcards.length}.`,
        });
        setLoading(false);
        return;
      }

      setGeneratedCards(flashcards);
    } catch (error) {
      console.error('Error generating flashcards:', error);
      setAlert({
        visible: true,
        title: 'Generation Failed',
        message: error.message || 'Failed to generate flashcards. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    if (generatedCards.length === 0 || !setId) {
      return;
    }

    try {
      // Use AI flashcards
      await useAIFlashcards(generatedCards.length);
      
      // Add all flashcards to the set
      addMultipleFlashcards(setId, generatedCards);
      
      // Replace current screen with set detail screen (removes document screen from history)
      navigation.replace('SetDetail', { setId });
    } catch (error) {
      console.error('Error saving flashcards:', error);
      setAlert({
        visible: true,
        title: 'Error',
        message: 'Failed to save flashcards. Please try again.',
      });
    }
  };

  const characterCount = documentText.length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.contentContainer}>
          <View style={[styles.content, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <View style={styles.iconContainer}>
              <View style={[styles.iconCircle, { backgroundColor: theme.primaryLight }]}>
                <Description sx={{ fontSize: 48, color: theme.primary }} />
              </View>
            </View>
            
            <Text style={[styles.title, { color: theme.text }]}>{t('flashcard.documentToFlashcards')}</Text>
            <Text style={[styles.subtitle, { color: theme.textSecondary, display: 'block' }]}>{t('flashcard.documentToFlashcardsSubtitle')}</Text>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <View style={styles.labelRow}>
                  <Text style={[styles.inputLabel, { color: theme.text }]}>{t('flashcard.documentContent')}</Text>
                  <Text style={[styles.characterCount, { color: theme.textMuted }]}>
                    {characterCount} {t('flashcard.characters')}
                  </Text>
                </View>
                <TextInput
                  style={[styles.textInput, styles.textArea, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={documentText}
                  onChangeText={setDocumentText}
                  placeholder={t('flashcard.documentPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={10}
                />
              </View>
            </View>

            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[styles.clearButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => setDocumentText('')}
              >
                <Text style={[styles.clearButtonText, { color: theme.text }]}>{t('flashcard.clear')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.generateButton, { backgroundColor: theme.primary }, (loading || !documentText.trim()) && styles.disabledButton]}
                onPress={handleGenerate}
                disabled={loading || !documentText.trim()}
              >
                <Description sx={{ fontSize: 20, color: 'white' }} />
                <Text style={[styles.generateButtonText, { color: 'white' }]}>
                  {loading ? t('flashcard.generating') : t('flashcard.generateCards')}
                </Text>
              </TouchableOpacity>
            </View>

            {generatedCards.length > 0 && (
              <View style={styles.generatedSection}>
                <Text style={[styles.generatedTitle, { color: theme.text }]}>
                  {t('flashcard.generatedCardsCount')}: {generatedCards.length}
                </Text>
                
                <View style={[styles.cardsPreviewContainer, { borderColor: theme.border, backgroundColor: theme.surface }]}>
                  <ScrollView 
                    style={styles.cardsPreviewScroll}
                    data-scrollbar-custom="true"
                    contentContainerStyle={styles.cardsPreview}
                  >
                    {generatedCards.map((card, index) => (
                      <View key={index} style={[styles.cardPreview, { backgroundColor: theme.background, borderColor: theme.border }]}>
                        <View style={styles.cardPreviewRow}>
                          <Text style={[styles.cardPreviewLabel, { color: theme.textMuted }]}>
                            {t('flashcard.question')}:
                          </Text>
                          <Text style={[styles.cardPreviewText, { color: theme.text }]}>
                            {card.front}
                          </Text>
                        </View>
                        <View style={styles.cardPreviewRow}>
                          <Text style={[styles.cardPreviewLabel, { color: theme.textMuted }]}>
                            {t('flashcard.answer')}:
                          </Text>
                          <Text style={[styles.cardPreviewText, { color: theme.text }]}>
                            {card.back}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </ScrollView>
                </View>

                <View style={styles.buttonContainer}>
                  <TouchableOpacity
                    style={[styles.button, styles.cancelButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                    onPress={() => {
                      setGeneratedCards([]);
                      setDocumentText('');
                    }}
                  >
                    <Text style={[styles.cancelButtonText, { color: theme.text }]}>{t('common.cancel')}</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.button, styles.saveButton, { backgroundColor: theme.primary }]}
                    onPress={handleSaveAll}
                  >
                    <Add sx={{ fontSize: 20, color: 'white' }} />
                    <Text style={[styles.saveButtonText, { color: 'white' }]}>
                      {t('flashcard.saveAllCards')}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      <CustomAlert
        visible={alert.visible}
        title={alert.title}
        message={alert.message}
        buttons={[
          {
            text: 'OK',
            onPress: () => {},
          },
        ]}
        onClose={() => setAlert({ ...alert, visible: false })}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    display: 'flex',
  },
  scrollContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    minHeight: '100vh',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 600,
  },
  content: {
    width: '100%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    margin: '0 auto',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'left',
    marginBottom: 32,
    lineHeight: 1.6,
    color: 'inherit',
  },
  form: {
    marginBottom: 24,
  },
  inputContainer: {
    marginBottom: 32,
  },
  labelRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 1.8,
  },
  characterCount: {
    fontSize: 14,
    lineHeight: 1.8,
  },
  textInput: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    transition: 'border-color 0.2s',
    lineHeight: 1.8,
  },
  textArea: {
    minHeight: 200,
    resize: 'vertical',
    lineHeight: 1.8,
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  clearButton: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  generateButton: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
  },
  generateButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  generatedSection: {
    marginTop: 32,
    paddingTop: 32,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  generatedTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 24,
  },
  cardsPreviewContainer: {
    borderRadius: 12,
    borderWidth: 1,
    maxHeight: 400,
    marginBottom: 24,
    overflow: 'hidden',
  },
  cardsPreviewScroll: {
    maxHeight: 400,
  },
  cardsPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
  },
  cardPreview: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  cardPreviewRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
    alignItems: 'flex-start',
  },
  cardPreviewLabel: {
    fontSize: 14,
    fontWeight: '600',
    flexShrink: 0,
    minWidth: 80,
    lineHeight: 1.8,
  },
  cardPreviewText: {
    fontSize: 14,
    lineHeight: 1.8,
    flex: 1,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, opacity 0.2s',
    cursor: 'pointer',
  },
  cancelButton: {
    borderWidth: 1,
  },
  saveButton: {
    gap: 8,
    borderWidth: 0,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default DocumentToFlashcardsScreen;
